<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else-if="hasPermission('products.view')"
      :iconHeader="hasPermission('products.add') ? 'add' : null"
      :title="$t('administration.products')"
      isSelectable
      :items="displayedProducts"
      :pagination="pagination"
      hideDefaultFooter
      :isInfinitScroll="true"
      :isSearchable="false"
      :headers="tableHeaders"
      :sortableColumns="sortableColumns"
      :notSortableColumns="notSortableColumns"
      :itemActions="actions"
      :isCustomIcon="true"
      :centerColumns="['action', 'SDS', 'bulletin', 'procedure']"
      data-cy="productTable"
      @sendQueryParams="fetchQuery"
      @iconAction="onClickAction"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('sites.placeholder.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
        <v-switch
          v-model="queryParams.includeInactive"
          :label="$t('clients.labels.includeInactive')"
          @change="onIncludeInactive"
        />
      </template>
      <template v-slot:item.code="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.code }}
        </div>
        <div v-else>
          {{ item.code }}
        </div>
      </template>
      <template v-slot:item.name="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.name }}
        </div>
        <div v-else>
          {{ item.name }}
        </div>
      </template>
      <template v-slot:item.pesticideNumber="{item}">
        <div
          v-if="item.active===false"
          :class="item.active===false && 'inactive'"
        >
          {{ item.pesticideNumber }}
        </div>
      </template>

      <template v-slot:item.procedure="{item}">
        <div v-if="item.procedure.some(file => file.lang === lang)">
          <ButtonIconTooltip
            v-if="getFileByLang(item.procedure)"
            :icon="'mdi-file-pdf-box'"
            :tooltip="getFileByLang(item.procedure).title"
            @click.stop.prevent="onClickFile(item.procedure)"
          />
        </div>
      </template>
      <template v-slot:item.bulletin="{item}">
        <div v-if="item.bulletin.some(file => file.lang === lang)">
          <ButtonIconTooltip
            v-if="getFileByLang(item.bulletin)"
            :icon="'mdi-file-pdf-box'"
            :tooltip="getFileByLang(item.bulletin).title"
            @click.stop.prevent="onClickFile(item.bulletin)"
          />
        </div>
      </template>
      <template v-slot:item.SDS="{item}">
        <div v-if="item.SDS.some(file => file.lang === lang)">
          <ButtonIconTooltip
            v-if="getFileByLang(item.SDS)"
            :icon="'mdi-file-pdf-box'"
            :tooltip="getFileByLang(item.SDS).title"
            @click.stop.prevent="onClickFile(item.SDS)"
          />
        </div>
      </template>


      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import ProductForm from '@/components/forms/ProductForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ButtonIconTooltip from '@/components/shared/widgets/ButtonIconTooltip';

export default {
    components: {
        TableRemote,
        SearchWithFilters,
        ButtonIconTooltip,
    },
    data() {
        return {
            products: [],
            queryParams: { perpage, sort: 'name_asc', includeInactive: false, search: '' },
            pagination: {},
            loading: false,
            filterName: 'ProductPage',
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'lang',
        ]),
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`products.labels.${header}`),
                value: header,
            }));
        },
        headers() {
            return [
                'code',
                'name',
                'pesticide_number',
                'SDS',
                'bulletin',
                'procedure',
            ];
        },
        sortableColumns() {
            return [
                'code',
                'name',
                'pesticide_number',
            ];
        },
        notSortableColumns() {
            return [
                'SDS',
                'bulletin',
                'procedure',
            ];
        },
        displayedProducts() {
            return this.products.map(product => ({
                code: product.code,
                id: product.id,
                active: product.active,
                name: product.name,
                pesticide_number: product.pesticideNumber,
                SDS: product.msds,
                bulletin: product.report,
                procedure: product.procedure,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('products.edit')) {
                actions.push({
                    name: this.$t('products.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('products.remove')) {
                actions.push({
                    name: this.$t('products.btn.active'),
                    icon: 'mdi-check-circle-outline',
                    mode: 'status',
                    customValidationProperty: 'active',
                    showOncustomValidationProperty: false,
                });

                actions.push({
                    name: this.$t('products.btn.inactive'),
                    icon: 'mdi-close-circle-outline',
                    mode: 'status',
                    customValidationProperty: 'active',
                    showOncustomValidationProperty: true,
                });
            }
            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getProducts();
        this.loading = false;
    },
    methods: {
        async getProducts() {
            const { data } = await this.$rq.getProducts(this.queryParams);
            this.products = [...this.products, ...data.products];
            this.pagination = data.pagination;
        },
        async fetchQuery(queryParams = {}) {
            this.queryParams = { ...this.queryParams, ...queryParams, search: queryParams?.search || '', page: 1 };
            this.pagination = {};
            this.products = [];
            await this.getProducts();
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.$root.$emit('showModal', {
                    title: this.$t('products.addProduct'),
                    showLangs: true,
                    component: Vue.component('ProductForm', ProductForm),
                    onConfirm: requestPayload => this.addProduct(requestPayload),
                });
            } else if (action && action.mode === 'edit') {
                const product = await this.getProduct(item.id);
                this.defaultData = cloneDeep(product);
                this.$root.$emit('showModal', {
                    title: this.$t('products.editProduct'),
                    component: Vue.component('editProduct', ProductForm),
                    showLangs: true,
                    payload: {
                        populateWith: {
                            ...product,
                        },
                        isEditable: true,
                    },
                    onConfirm: async requestPayload => this.updateProduct(requestPayload),
                });
            } else if (action && action.mode === 'status') {
                this.$root.$emit('showModal', {
                    title: this.$t(`products.${item.active ? 'inactive' : 'active'}`),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t(`products.${item.active ? 'deactivateMessage' : 'activateMessage'}`) },
                    onConfirm: () => this.updateActive(item),
                    isForm: false,
                });
            }
        },
        async updateActive(item) {
            await this.$rq.updateProductStatus(item.id, { status: !item.active });
            await this.fetchQuery();
        },
        async updateProduct(payload) {
            const requestPayload = this.$utils.getObjectsDifference(payload, this.defaultData);
            const { id, name } = payload;
            const paylaodToSend = { ...requestPayload, description: payload.description };
            if (isEmpty(paylaodToSend.description)) {
                // eslint-disable-next-line no-param-reassign
                delete paylaodToSend.description;
            }
            const request = this.getRequest(paylaodToSend);
            await this.$rq.updateProduct(id, request);

            this.$toasted.global.toast_success({
                message: this.$t('msg.productModified', { data: name }),
            });
            this.fetchQuery();
        },
        async deleteProduct(item) {
            await this.$rq.deleteProduct(item.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.productRemoved', { data: item.name }),
            });
            this.fetchQuery();
        },
        async addProduct(requestPayload) {
            if (isEmpty(requestPayload.description)) {
                // eslint-disable-next-line no-param-reassign
                delete requestPayload.description;
            }
            const request = this.getRequest(requestPayload);
            await this.$rq.addProduct(request);
            this.$toasted.global.toast_success({
                message: this.$t('msg.productAdded'),
            });

            this.fetchQuery();
        },
        async getProduct(productId) {
            const { data } = await this.$rq.getProduct(productId, { showallattributes: true });
            const { files, ...props } = data;

            return {
                ...props,
                ...files,
            };
        },
        getRequest(payload) {
            const request = new FormData();
            Object.keys(payload).forEach(key => {
                if (typeof payload[key] === 'object') {
                    const isFile = payload[key] instanceof File;
                    if (isFile) {
                        request.append(key, payload[key]);
                    } else {
                        request.append(key, JSON.stringify(payload[key]));
                    }
                } else {
                    // undefined is when the file is deleted from the Form
                    request.append(key, typeof payload[key] === 'undefined' ? '' : payload[key]);
                }
            });
            return request;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isSearched) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getProducts();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        async onIncludeInactive() {
            this.loading = true;
            await this.fetchQuery();
            this.loading = false;
        },
        async handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 1,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.products = [];
            this.isSearched = true;
            await this.getProducts();
            this.isSearched = false;
        },
        getFileByLang(arrayOfFiles) {
            return arrayOfFiles?.find(file => file.lang === this.lang);
        },
        async onClickFile(item) {
            const { data } = await this.$rq.downloadFile(this.getFileByLang(item).file || this.getFileByLang(item).id);
            this.$utils.saveFileOnDisk(data, this.getFileByLang(item).title);
        },
    },
};

</script>
